import { baseApi, API_TAGS, createCacheTags, createEndpointBuilder, type Params } from '@/shared/api';
import {
  type ConsignmentsPageResponse,
  type ConsignmentByIdRequest,
  type ConsignmentsListParams,
  type ConsignmentBulkProcessRequest,
  type ConsignmentBulkProcessResponse,
  type BulkProcessParams,
  type ConsignmentUpdateRequest,
  type ConsignmentUpdateParams,
  type ConsignmentSummary,
  type ConsignmentHistoryEntry,
  type ConsignmentHistoryParams,
  type ConsignmentDisposition,
  type RawDisposition,
  type RawDispositionResponse,
  type ConsignmentAttachmentUploadParams,
  type ConsignmentAttachmentUploadRequest,
  type ConsignmentAttachmentUploadResponse,
  type ConsignmentLineDetailParams,
  type ConsignmentLineDetailResponse,
  type ConsignmentsInfiniteQueryArgs
} from '../model/types';

const consignmentsEndpoint = createEndpointBuilder('/api/v2/consignments');

export const consignmentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getConsignments: builder.infiniteQuery<ConsignmentsPageResponse, ConsignmentsListParams, number>({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          const nextPage = lastPageParam + 1;
          if (nextPage >= lastPage.totalPages) {
            return undefined;
          }
          return nextPage;
        }
      },
      query: ({ queryArg, pageParam }) => ({
        url: consignmentsEndpoint(''),
        params: {
          ...queryArg,
          page: pageParam
        }
      }),
      providesTags: (_result, _error, { queryArg }) =>
        createCacheTags({
          baseTag: API_TAGS.CONSIGNMENTS,
          params: queryArg as unknown as Params
        })
    }),

    getConsignmentById: builder.query<ConsignmentSummary, ConsignmentByIdRequest>({
      query: ({ id, projectId, siteId }) => ({
        url: consignmentsEndpoint(`/${id}`),
        params: { projectId, siteId }
      }),
      providesTags: (_result, _error, { id }) => [API_TAGS.CONSIGNMENTS, { type: API_TAGS.CONSIGNMENTS, id }]
    }),

    bulkProcessConsignments: builder.mutation<
      ConsignmentBulkProcessResponse,
      ConsignmentBulkProcessRequest & BulkProcessParams
    >({
      query: ({ projectId, siteId, ...body }) => ({
        url: consignmentsEndpoint('/bulk-process'),
        method: 'PUT',
        params: { projectId, siteId },
        body
      }),
      invalidatesTags: [API_TAGS.CONSIGNMENTS]
    }),

    updateConsignment: builder.mutation<ConsignmentSummary, ConsignmentUpdateRequest & ConsignmentUpdateParams>({
      query: ({ id, projectId, siteId, ...body }) => ({
        url: consignmentsEndpoint(`/${id}`),
        method: 'PUT',
        params: { projectId, siteId },
        body
      }),
      invalidatesTags: (_result, _error, { id }) => [API_TAGS.CONSIGNMENTS, { type: API_TAGS.CONSIGNMENTS, id }]
    }),

    getConsignmentHistory: builder.query<ConsignmentHistoryEntry[], ConsignmentHistoryParams>({
      query: ({ id, projectId, siteId, type }) => ({
        url: consignmentsEndpoint(`/${id}/history`),
        params: {
          projectId,
          siteId,
          ...(type && { type })
        }
      }),
      providesTags: (_result, _error, params) =>
        createCacheTags({
          baseTag: API_TAGS.CONSIGNMENT_HISTORY,
          params
        })
    }),

    getConsignmentDispositions: builder.query<ConsignmentDisposition[], ConsignmentByIdRequest>({
      query: ({ id, projectId, siteId }) => ({
        url: consignmentsEndpoint(`/${id}/dispositions`),
        params: { projectId, siteId }
      }),
      transformResponse: (response: RawDispositionResponse | RawDisposition[]) => {
        const mapDisposition = (line: RawDisposition): ConsignmentDisposition => ({
          itemCode: line.itemCode || line.productCode || '-',
          description: line.description || line.productDescription || '-',
          unit: line.unit || line.uom || '-',
          totalPerSrf: typeof line.totalPerSrf === 'number' ? line.totalPerSrf : 0,
          remainsWithSender: line.remainsWithSender || 0,
          cancelledBySender: line.cancelledBySender || 0,
          receivedFromSender: line.receivedFromSender || 0,
          transferOut: line.transferOut || 0,
          transferIn: line.transferIn || 0,
          stockOnHand: line.stockOnHand || 0,
          reportedLost: line.reportedLost || 0,
          releasedToReceiver: line.releasedToReceiver || 0,
          o: line.o,
          locations: (line.locations || []).map((loc) => ({
            locationName: loc.locationName || '-',
            received: loc.received || 0,
            transferOut: loc.transferOut || 0,
            transferIn: loc.transferIn || 0,
            stockOnHand: loc.stockOnHand || 0,
            reportedLost: loc.reportedLost || 0,
            released: loc.released || 0,
            movements: (loc.movements || []).map((mov) => ({
              date: mov.date || '-',
              description: mov.description || null,
              quantity: mov.quantity || 0,
              stockOnHand: mov.stockOnHand || 0
            }))
          }))
        });

        if (response && !Array.isArray(response)) {
          if (Array.isArray(response.lines)) return response.lines.map(mapDisposition);
          if (Array.isArray(response.results)) return response.results.map(mapDisposition);
        }
        if (Array.isArray(response)) {
          return response.map(mapDisposition);
        }
        return [];
      },
      providesTags: (_result, _error, { id }) => [API_TAGS.CONSIGNMENTS, { type: API_TAGS.CONSIGNMENTS, id }]
    }),

    uploadConsignmentAttachment: builder.mutation<
      ConsignmentAttachmentUploadResponse,
      ConsignmentAttachmentUploadParams & ConsignmentAttachmentUploadRequest
    >({
      query: ({ id, projectId, siteId, file, attachmentType, comment }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('attachmentType', attachmentType);
        if (comment) {
          formData.append('comment', comment);
        }

        return {
          url: consignmentsEndpoint(`/${id}/attachments`),
          method: 'POST',
          params: { projectId, siteId },
          body: formData
        };
      },
      invalidatesTags: (_result, _error, { id }) => [API_TAGS.CONSIGNMENTS, { type: API_TAGS.CONSIGNMENTS, id }]
    }),

    getConsignmentLineDetail: builder.query<ConsignmentLineDetailResponse, ConsignmentLineDetailParams>({
      query: ({ lineId, projectId, siteId }) => ({
        url: consignmentsEndpoint(`/lines/${lineId}`),
        params: { projectId, siteId }
      }),
      providesTags: (_result, _error, { lineId }) => [
        API_TAGS.CONSIGNMENTS,
        { type: API_TAGS.CONSIGNMENTS, id: `line-${lineId}` }
      ]
    }),
    downloadConsignmentReport: builder.mutation<Blob, { id: string; lang?: string; opt?: number }>({
      query: ({ id, lang = 'en', opt = 1 }) => ({
        url: `/api/v1/reports/consignment-report/${id}`,
        params: { lang, opt },
        responseHandler: (response) => response.blob(),
        cache: 'no-cache'
      })
    }),

    downloadShippingLabels: builder.mutation<Blob, { id: string; lang?: string; opt?: number; o?: number }>({
      query: ({ id, lang = 'en', opt = 1, o }) => ({
        url: `/api/v1/reports/consignment-shipping-label/${id}`,
        params: { lang, opt, o },
        responseHandler: (response) => response.blob(),
        cache: 'no-cache'
      })
    }),
    downloadConsignmentTag: builder.mutation<Blob, { id: string; lang?: string; opt?: number; ext?: number }>({
      query: ({ id, lang = 'en', opt = 1, ext }) => ({
        url: `/api/v1/reports/consignment-tag/${id}`,
        params: { lang, opt, ext },
        responseHandler: (response) => response.blob(),
        cache: 'no-cache'
      })
    })
  })
});

export const {
  useGetConsignmentByIdQuery,
  useGetConsignmentsInfiniteQuery,
  useBulkProcessConsignmentsMutation,
  useUpdateConsignmentMutation,
  useGetConsignmentHistoryQuery,
  useGetConsignmentDispositionsQuery,
  useUploadConsignmentAttachmentMutation,
  useGetConsignmentLineDetailQuery,
  useDownloadConsignmentReportMutation,
  useDownloadShippingLabelsMutation,
  useDownloadConsignmentTagMutation
} = consignmentsApi;
